/**
 * CourseLessonForm — create/edit a CourseLesson.
 * Includes pedagogy fields: learningObjectives, teacherNotes, homeworkSummary.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { Loader2 } from 'lucide-react';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';

const courseLessonFormSchema = z.object({
  title: z.string().min(1, 'Bắt buộc').max(300),
  description: z.string().optional().or(z.literal('')),
  content: z.string().optional().or(z.literal('')),
  learningObjectives: z.string().optional().or(z.literal('')),
  teacherNotes: z.string().optional().or(z.literal('')),
  homeworkSummary: z.string().optional().or(z.literal('')),
  suggestedDurationMin: z.coerce
    .number()
    .int()
    .positive('Phải ≥ 1')
    .optional()
    .or(z.literal('')),
});

export type CourseLessonFormValues = z.infer<typeof courseLessonFormSchema>;

interface CourseLessonFormProps {
  defaultValues?: Partial<CourseLessonFormValues>;
  isSubmitting?: boolean;
  onSubmit: (values: CourseLessonFormValues) => void;
  onCancel: () => void;
}

export function CourseLessonForm({
  defaultValues,
  isSubmitting,
  onSubmit,
  onCancel,
}: CourseLessonFormProps) {
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CourseLessonFormValues>({
    resolver: zodResolver(courseLessonFormSchema),
    defaultValues: {
      title: '',
      description: '',
      content: '',
      learningObjectives: '',
      teacherNotes: '',
      homeworkSummary: '',
      suggestedDurationMin: '',
      ...defaultValues,
    },
  });

  useEffect(() => {
    reset({
      title: '',
      description: '',
      content: '',
      learningObjectives: '',
      teacherNotes: '',
      homeworkSummary: '',
      suggestedDurationMin: '',
      ...defaultValues,
    });
  }, [defaultValues, reset]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="flex flex-col gap-4">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="lesson-title" className="text-sm font-medium">
          Tiêu đề bài học <span className="text-destructive">*</span>
        </Label>
        <Input
          id="lesson-title"
          className="h-10"
          placeholder="Lesson 1: Greetings"
          {...register('title')}
        />
        {errors.title && <p className="text-xs text-destructive">{errors.title.message}</p>}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="lesson-duration" className="text-sm font-medium">
          Thời lượng đề xuất (phút)
        </Label>
        <Input
          id="lesson-duration"
          type="number"
          min={1}
          className="h-10"
          placeholder="90"
          {...register('suggestedDurationMin')}
        />
        {errors.suggestedDurationMin && (
          <p className="text-xs text-destructive">{errors.suggestedDurationMin.message}</p>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="lesson-description" className="text-sm font-medium">Mô tả</Label>
        <Textarea
          id="lesson-description"
          rows={2}
          placeholder="Giới thiệu bài học..."
          {...register('description')}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="lesson-objectives" className="text-sm font-medium">Mục tiêu học tập</Label>
        <Textarea
          id="lesson-objectives"
          rows={3}
          placeholder="Sau bài học này, học viên có thể..."
          {...register('learningObjectives')}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="lesson-content" className="text-sm font-medium">Nội dung bài học</Label>
        <Textarea
          id="lesson-content"
          rows={4}
          placeholder="Nội dung chi tiết (hỗ trợ Markdown)..."
          {...register('content')}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="lesson-teacher-notes" className="text-sm font-medium">Ghi chú giáo viên</Label>
        <Textarea
          id="lesson-teacher-notes"
          rows={3}
          placeholder="Lưu ý dành cho giáo viên..."
          {...register('teacherNotes')}
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="lesson-homework" className="text-sm font-medium">Tóm tắt bài tập về nhà</Label>
        <Textarea
          id="lesson-homework"
          rows={2}
          placeholder="Bài tập về nhà cho buổi này..."
          {...register('homeworkSummary')}
        />
      </div>

      <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end pt-2">
        <Button type="button" variant="ghost" onClick={onCancel} disabled={isSubmitting}>
          Hủy
        </Button>
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Lưu'}
        </Button>
      </div>
    </form>
  );
}
